import React from 'react';
import {
    Kanban, LayoutPanelLeft, GanttChart,
    Calendar, ChevronLeft, ChevronRight,
    Search, RefreshCw, UserCheck,
} from 'lucide-react';
// `monthKey` is a YYYY-MM string used as the canonical filter value. The maths lives
// in utils/monthKey.js — a toolbar renders, it does not own date arithmetic.
import { currentMonthKey, shiftMonthKey, formatMonthLabel } from '../utils/monthKey';

/**
 * Shared toolbar used by JobsPage (Kanban), JobsTabletView and JobsPlannerPage.
 *
 * <p>The visual goal is a stable header — when the user switches between
 * Kanban / Tablet / Planner the eye should perceive the same toolbar with only
 * the active button changing. Any view-specific controls go through the
 * {@code rightExtras} slot so they sit in the same area but don't disturb the
 * shared cells (view switcher, month picker, search, refresh).
 *
 * <p>The control row is fixed at h-14. View-specific status filters (kanban
 * stats, tablet column filters) go into the {@code titleExtras} slot, which
 * renders as a second h-10 row beneath it rather than competing with the
 * controls for horizontal space. The slot may be empty — the planner leaves it
 * so — in which case no second row is rendered.
 *
 * <p>Filter state is owned by each parent component (this is intentional —
 * lifting it to a single orchestrator was considered but rejected as too
 * invasive). Each view passes its own values and setters; the shape is
 * uniform so the visible controls behave identically across views.
 */

// ── Sub-components ──

/**
 * Three-way segmented view switcher. Active view is visually pressed; clicking
 * a non-active option calls the matching setter. Buttons are sized identically
 * to keep the control's overall width stable.
 */
const ViewSwitcher = ({ active, onKanban, onTablet, onPlanner }) => {
    const item = (id, Icon, label, onClick) => {
        const isActive = active === id;
        return (
            <button
                type="button"
                onClick={onClick}
                aria-pressed={isActive}
                title={label}
                className={`flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold transition-colors
                    ${isActive
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'text-violet-700 hover:bg-violet-100'}`}
            >
                <Icon size={15} />
                <span className="hidden md:inline">{label}</span>
            </button>
        );
    };
    return (
        <div className="flex items-center gap-0.5 bg-violet-50 rounded-xl p-1 shrink-0">
            {item('kanban',  Kanban,          'Board',   onKanban)}
            {item('tablet',  LayoutPanelLeft, 'Split',   onTablet)}
            {item('planner', GanttChart,      'Planner', onPlanner)}
        </div>
    );
};

/**
 * Compact month-filter pill — prev / picker / next.
 *
 * <p>The native month input is layered on top of the visible label so users
 * can either step ±1 month with the chevrons or jump directly via the OS
 * month picker. The "Today" reset link only renders when the user has moved
 * away from the current month.
 */
const MonthFilter = ({ selectedMonth, setSelectedMonth }) => (
    <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-0.5 shrink-0">
        <button
            type="button"
            onClick={() => setSelectedMonth(shiftMonthKey(selectedMonth, -1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600"
            title="Previous month"
        >
            <ChevronLeft size={14} />
        </button>
        <label className="relative flex items-center gap-1.5 px-2 h-7 cursor-pointer hover:bg-gray-50 rounded-lg">
            <Calendar size={13} className="text-violet-600" />
            <span className="text-xs font-medium text-gray-700 select-none whitespace-nowrap">
                {formatMonthLabel(selectedMonth)}
            </span>
            <input
                type="month"
                value={selectedMonth}
                onChange={(e) => {
                    // Native month input emits "" when cleared on some browsers —
                    // guard against that so we always have a valid one-month window.
                    if (e.target.value) setSelectedMonth(e.target.value);
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label="Select month"
            />
        </label>
        <button
            type="button"
            onClick={() => setSelectedMonth(shiftMonthKey(selectedMonth, 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600"
            title="Next month"
        >
            <ChevronRight size={14} />
        </button>
        {selectedMonth !== currentMonthKey() && (
            <button
                type="button"
                onClick={() => setSelectedMonth(currentMonthKey())}
                className="text-[11px] px-2 h-7 text-violet-600 hover:bg-violet-50 rounded-lg font-semibold"
                title="Jump to current month"
            >
                Today
            </button>
        )}
    </div>
);

// ── Main toolbar ──

/**
 * Stable jobs toolbar.
 *
 * Required props:
 *   activeView           — 'kanban' | 'tablet' | 'planner'
 *   onSwitchView(id)     — invoked when the user picks a different view
 *
 * Optional filter props (each view passes the ones it owns):
 *   selectedMonth, setSelectedMonth      — shows the month-filter pill when both supplied
 *   searchQuery, setSearchQuery          — shows the search input when both supplied
 *   myJobsOnly, setMyJobsOnly            — shows the My Jobs toggle when both supplied
 *   onRefresh, refreshing                — shows the refresh button when onRefresh supplied
 *
 * Slot props:
 *   titleExtras   — rendered as a second row below the controls (status pills)
 *   rightExtras   — rendered immediately before the refresh button (view-specific
 *                   controls like Today/Zoom/Fullscreen)
 *
 * Decorative props:
 *   subtitle      — small text below the title (active count, current time, etc.)
 *
 * Wall-display prop:
 *   chromeless    — TV Mode. Drops the desk furniture a wall has no use for: the
 *                   Board/Split/Planner switcher and the title block. Everything that
 *                   DOES something on a wall stays — the month stepper, the refresh,
 *                   and Exit TV above all, which is the only way back.
 */
const JobsToolbar = ({
                         activeView,
                         onSwitchView,
                         title = 'Jobs Board',
                         subtitle = null,
                         titleExtras = null,
                         rightExtras = null,
                         selectedMonth = null,
                         setSelectedMonth = null,
                         searchQuery = null,
                         setSearchQuery = null,
                         myJobsOnly = null,
                         setMyJobsOnly = null,
                         onRefresh = null,
                         refreshing = false,
                         chromeless = false,
                     }) => {
    const showMonth   = selectedMonth != null && setSelectedMonth != null;
    const showSearch  = searchQuery   != null && setSearchQuery   != null;
    const showMyJobs  = myJobsOnly    != null && setMyJobsOnly    != null;
    const showRefresh = typeof onRefresh === 'function';

    return (
        <div className="shrink-0 bg-white border-b border-gray-200">
        <div className="h-14 flex items-center gap-3 px-4">

            {/* Left cluster — view switcher + title block. The title never
                shrinks; with the status filters on their own row there is room
                for it, so it no longer truncates to "Jobs B…".

                🔑 Both go in TV Mode: a wall display cannot switch views and does not
                need to be told which screen it is. The flex-1 spacer below keeps the
                right-hand controls exactly where they sit now, so removing these does
                not drag Exit TV and Refresh across the toolbar. */}
            {!chromeless && (
                <>
                    <ViewSwitcher
                active={activeView}
                        onKanban={() => onSwitchView('kanban')}
                        onTablet={() => onSwitchView('tablet')}
                        onPlanner={() => onSwitchView('planner')}
                    />

                    <div className="hidden lg:flex flex-col justify-center leading-tight shrink-0">
                        <h1 className="text-sm font-bold text-gray-900 whitespace-nowrap">{title}</h1>
                        {subtitle && (
                            <p className="text-[11px] text-gray-400 whitespace-nowrap">{subtitle}</p>
                        )}
                    </div>
                </>
            )}

            <div className="flex-1 min-w-0" />

            {/* Right cluster — filters then view-specific extras then refresh */}
            {showMonth && (
                <MonthFilter
                    selectedMonth={selectedMonth}
                    setSelectedMonth={setSelectedMonth}
                />
            )}

            {showSearch && (
                <div className="relative shrink-0">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search jobs…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 pr-3 h-9 border border-gray-200 rounded-xl text-sm w-36 xl:w-44 focus:outline-none focus:ring-2 focus:ring-violet-500 bg-gray-50"
                    />
                </div>
            )}

            {showMyJobs && (
                <button
                    type="button"
                    onClick={() => setMyJobsOnly(v => !v)}
                    aria-pressed={myJobsOnly}
                    title={myJobsOnly ? 'Showing only my assigned jobs' : 'Show only jobs assigned to me'}
                    className={`flex items-center gap-1.5 h-9 px-3 rounded-xl text-xs font-semibold border transition-colors shrink-0
                        ${myJobsOnly
                        ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                        : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'}`}
                >
                    <UserCheck size={14} />
                    <span className="hidden md:inline">My jobs</span>
                </button>
            )}

            {rightExtras}

            {showRefresh && (
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={refreshing}
                    className="h-9 w-9 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors shrink-0"
                    title="Refresh"
                >
                    <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
                </button>
            )}
        </div>

        {/* Second row — status filters. Given a row of their own they never
            compete with the view switcher and month/search controls above; the
            row scrolls horizontally (bar hidden) on narrow screens. */}
        {titleExtras && (
            <div className="h-10 flex items-center gap-1.5 px-4 border-t border-gray-100 overflow-x-auto no-scrollbar">
                {titleExtras}
            </div>
        )}
        </div>
    );
};

export default JobsToolbar;